import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Plus,
  Trash2,
  Edit2,
  Copy,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Calendar,
  Save,
  X,
  Mail,
  Link2,
  Filter,
} from 'lucide-react';
import GlassCard from '../ui/GlassCard';
import GlassButton from '../ui/GlassButton';
import GlassInput from '../ui/GlassInput';
import GlassSelect from '../ui/GlassSelect';
import GlassTabs from '../ui/GlassTabs';
import AvailabilityEditorCore from './AvailabilityEditorCore';
import BookingBrandingModal from './BookingBrandingModal';
import {
  bookingApi,
  BookingResourceItem,
  BookingTypeItem,
  BookingAvailabilityRow,
  Appointment,
  AssignableUser,
  getProjects,
  Project,
  getCustomDomains,
} from '../../services/api';
import './Booking.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  return fallback;
};

const STATUS_COLORS: Record<string, string> = {
  booked: 'var(--primary)',
  completed: 'var(--success, #16a34a)',
  cancelled: 'var(--danger)',
  no_show: 'var(--warning, #d97706)',
};

const formatUtcInTimezone = (utc: string, tz: string): string => {
  try {
    return new Date(utc).toLocaleString('en-AU', {
      timeZone: tz || 'Australia/Sydney',
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return utc;
  }
};

// ─── AvailabilityEditor ───────────────────────────────────────────────────────
// Thin admin wrapper around AvailabilityEditorCore (weekly windows only — blackouts
// are managed inline in ResourceForm, not here). Save logic uses bookingApi.setAvailability.

interface AvailabilityEditorProps {
  resourceId: string;
  initialRows: BookingAvailabilityRow[];
  onSaved: (rows: BookingAvailabilityRow[]) => void;
}

const AvailabilityEditor: React.FC<AvailabilityEditorProps> = ({ resourceId, initialRows, onSaved }) => {
  const [rows, setRows] = useState<BookingAvailabilityRow[]>(initialRows);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const save = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const updated = await bookingApi.setAvailability(resourceId, rows);
      setSuccess('Availability saved.');
      onSaved(updated.availability ?? rows);
    } catch (e: any) {
      setError(getApiError(e, 'Failed to save availability.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <AvailabilityEditorCore rows={rows} onRowsChange={setRows} dayLabels="short" />
      {error && (
        <div className="fi-error" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {success && (
        <div style={{ color: 'var(--success, #16a34a)', fontSize: 'var(--text-sm)' }}>{success}</div>
      )}
      <GlassButton variant="primary" onClick={save} disabled={saving}>
        <Save size={14} /> {saving ? 'Saving...' : 'Save Availability'}
      </GlassButton>
    </div>
  );
};

// ─── ResourceForm ─────────────────────────────────────────────────────────────

interface ResourceFormProps {
  initial?: Partial<BookingResourceItem>;
  assignableUsers: AssignableUser[];
  onSave: (data: any) => Promise<void>;
  onCancel: () => void;
}

const ResourceForm: React.FC<ResourceFormProps> = ({ initial, assignableUsers, onSave, onCancel }) => {
  const [name, setName] = useState(initial?.name ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [timezone, setTimezone] = useState(initial?.timezone ?? 'Australia/Sydney');
  const [crmUserId, setCrmUserId] = useState(initial?.crm_user_id ?? '');
  const [blackoutDates, setBlackoutDates] = useState<string[]>(initial?.blackout_dates ?? []);
  const [newBlackout, setNewBlackout] = useState('');
  const [calendarProvider, setCalendarProvider] = useState<string>(initial?.calendar_provider ?? '');
  const [calendarId, setCalendarId] = useState(initial?.calendar_id ?? '');
  const [calendarOwner, setCalendarOwner] = useState(initial?.user_id ?? '');
  const [projectId, setProjectId] = useState(initial?.project_id ?? '');
  const [projects, setProjects] = useState<Project[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getProjects(false).then(setProjects).catch(() => {});
  }, []);

  const addBlackout = () => {
    if (newBlackout && !blackoutDates.includes(newBlackout)) {
      setBlackoutDates((prev) => [...prev, newBlackout].sort());
    }
    setNewBlackout('');
  };

  const removeBlackout = (d: string) => {
    setBlackoutDates((prev) => prev.filter((x) => x !== d));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave({
        name: name.trim(),
        email: email.trim() || undefined,
        timezone: timezone.trim() || 'Australia/Sydney',
        crm_user_id: crmUserId.trim() || undefined,
        blackout_dates: blackoutDates,
        calendar_provider: (calendarProvider as 'ms' | 'google') || null,
        calendar_id: calendarId.trim() || null,
        user_id: calendarOwner,
        project_id: projectId || null,
      });
    } catch (e: any) {
      setError(getApiError(e, 'Save failed.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <GlassInput label="Name *" value={name} onChange={setName} placeholder="e.g. Jane Smith" />
      <GlassInput label="Email" value={email} onChange={setEmail} placeholder="jane@example.com" />
      <GlassInput label="Timezone (IANA)" value={timezone} onChange={setTimezone} placeholder="Australia/Sydney" />
      <GlassInput label="CRM User ID (optional)" value={crmUserId} onChange={setCrmUserId} placeholder="SuiteCRM user id" />

      <GlassSelect
        label="Project (for branding)"
        value={projectId}
        onChange={setProjectId}
        options={[
          { value: '', label: 'None (no branding)' },
          ...projects.map((p) => ({ value: p.project_id, label: p.name })),
        ]}
      />
      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 'calc(-1 * var(--space-2))' }}>
        Booking types on this resource inherit their public booking-page branding (logo, colours) from this project's Brand Guide.
      </div>

      <GlassSelect
        label="Calendar provider (for booking sync)"
        value={calendarProvider}
        onChange={setCalendarProvider}
        options={[
          { value: '', label: 'None' },
          { value: 'ms', label: 'Microsoft 365' },
          { value: 'google', label: 'Google' },
        ]}
      />
      {calendarProvider && (
        <>
          <GlassInput
            label="Calendar ID (optional)"
            value={calendarId}
            onChange={setCalendarId}
            placeholder="primary / me"
          />
          <GlassSelect
            label="Calendar owner (team member)"
            value={calendarOwner}
            onChange={setCalendarOwner}
            options={[
              { value: '', label: 'Me (default)' },
              ...assignableUsers.map((u) => ({
                value: u.user_id,
                label: `${u.full_name} — ${u.email}`,
              })),
            ]}
          />
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 'calc(-1 * var(--space-2))' }}>
            Whose connected calendar this resource syncs against.
          </div>
        </>
      )}

      <div>
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-1)' }}>
          Blackout Dates
        </div>
        {blackoutDates.map((d) => (
          <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-1)' }}>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>{d}</span>
            <button type="button" onClick={() => removeBlackout(d)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)' }}>
              <X size={12} />
            </button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          <input
            type="date"
            className="bkm-time-input"
            value={newBlackout}
            onChange={(e) => setNewBlackout(e.target.value)}
          />
          <GlassButton type="button" variant="secondary" onClick={addBlackout}>
            <Plus size={14} /> Add
          </GlassButton>
        </div>
      </div>

      {error && (
        <div className="fi-error" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <GlassButton type="submit" variant="primary" disabled={saving}>
          {saving ? 'Saving...' : 'Save Resource'}
        </GlassButton>
        <GlassButton type="button" variant="secondary" onClick={onCancel}>Cancel</GlassButton>
      </div>
    </form>
  );
};

// ─── ProjectFilterBar ─────────────────────────────────────────────────────────
// Shared "showing only <project>'s resources (+ unassigned)" indicator + toggle,
// reused on both the Resources and Booking Types tabs.

interface ProjectFilterBarProps {
  activeProjectName?: string | null;
  showAllProjects: boolean;
  onToggle: () => void;
}

const ProjectFilterBar: React.FC<ProjectFilterBarProps> = ({ activeProjectName, showAllProjects, onToggle }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      <Filter size={12} />
      {showAllProjects
        ? 'Showing resources from all projects.'
        : `Showing ${activeProjectName ? `"${activeProjectName}"` : 'this project'}'s resources, plus unassigned ones.`}
    </span>
    <GlassButton variant="secondary" onClick={onToggle}>
      {showAllProjects ? 'Show only current project' : 'Show all projects'}
    </GlassButton>
  </div>
);

// ─── ResourcesTab ─────────────────────────────────────────────────────────────

interface ResourcesTabProps {
  resources: BookingResourceItem[];
  loading: boolean;
  error: string;
  onRefresh: () => void;
  onCreated: (r: BookingResourceItem) => void;
  onUpdated: (r: BookingResourceItem) => void;
  onDeleted: (id: string) => void;
  activeProjectId?: string;
  activeProjectName?: string | null;
  showAllProjects: boolean;
  onToggleShowAll: () => void;
}

const ResourcesTab: React.FC<ResourcesTabProps> = ({
  resources, loading, error, onRefresh, onCreated, onUpdated, onDeleted,
  activeProjectId, activeProjectName, showAllProjects, onToggleShowAll,
}) => {
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedAvail, setExpandedAvail] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
  const [linkInfo, setLinkInfo] = useState<Record<string, { connectPath?: string; availabilityPath?: string; connectEmailSent?: boolean; availEmailSent?: boolean }>>({});
  const [actionError, setActionError] = useState('');
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [brandingResource, setBrandingResource] = useState<BookingResourceItem | null>(null);

  useEffect(() => {
    bookingApi.listAssignableUsers().then(setAssignableUsers).catch(() => {});
  }, []);

  const handleSendInvite = async (id: string) => {
    setBusyAction(`${id}:invite`);
    setActionError('');
    try {
      const res = await bookingApi.sendCalendarInvite(id);
      setLinkInfo((prev) => ({ ...prev, [id]: { ...prev[id], connectPath: res.connect_path, connectEmailSent: res.email_sent } }));
    } catch (e: any) {
      setActionError(getApiError(e, 'Failed to send calendar invite.'));
    } finally {
      setBusyAction(null);
    }
  };

  const handleRegenerateAvailability = async (id: string) => {
    setBusyAction(`${id}:avail`);
    setActionError('');
    try {
      const res = await bookingApi.regenerateAvailabilityLink(id);
      setLinkInfo((prev) => ({ ...prev, [id]: { ...prev[id], availabilityPath: res.availability_path, availEmailSent: res.email_sent } }));
    } catch (e: any) {
      setActionError(getApiError(e, 'Failed to regenerate availability link.'));
    } finally {
      setBusyAction(null);
    }
  };

  const handleRevoke = async (id: string) => {
    if (!window.confirm('Revoke this calendar connection? Booking sync will stop until reconnected.')) return;
    setBusyAction(`${id}:revoke`);
    setActionError('');
    try {
      await bookingApi.revokeCalendar(id);
      onRefresh();
    } catch (e: any) {
      setActionError(getApiError(e, 'Failed to revoke calendar.'));
    } finally {
      setBusyAction(null);
    }
  };

  const handleCreate = async (data: any) => {
    const r = await bookingApi.createResource(data);
    onCreated(r);
    setCreating(false);
  };

  const handleUpdate = async (id: string, data: any) => {
    const r = await bookingApi.updateResource(id, data);
    onUpdated(r);
    setEditingId(null);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this resource? All booking types will also be removed.')) return;
    setDeleteError('');
    try {
      await bookingApi.deleteResource(id);
      onDeleted(id);
    } catch (e: any) {
      setDeleteError(getApiError(e, 'Delete failed.'));
    }
  };

  if (loading) return <div style={{ color: 'var(--text-secondary)', padding: 'var(--space-4)' }}>Loading resources...</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
        <GlassButton variant="primary" onClick={() => { setCreating(true); setEditingId(null); }}>
          <Plus size={16} /> New Resource
        </GlassButton>
        <GlassButton variant="secondary" onClick={onRefresh}>
          <RefreshCw size={14} /> Refresh
        </GlassButton>
      </div>

      {activeProjectId && (
        <ProjectFilterBar activeProjectName={activeProjectName} showAllProjects={showAllProjects} onToggle={onToggleShowAll} />
      )}

      {error && <div className="fi-error"><AlertCircle size={14} /> {error}</div>}
      {deleteError && <div className="fi-error"><AlertCircle size={14} /> {deleteError}</div>}

      {creating && (
        <GlassCard>
          <h4 style={{ margin: '0 0 var(--space-3)', color: 'var(--text)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)' }}>
            New Resource
          </h4>
          <ResourceForm assignableUsers={assignableUsers} onSave={handleCreate} onCancel={() => setCreating(false)} />
        </GlassCard>
      )}

      {resources.length === 0 && !creating && (
        <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
          {activeProjectId && !showAllProjects
            ? (
              <>
                No resources for this project yet (and none unassigned). Other projects may have resources —{' '}
                <button
                  type="button"
                  onClick={onToggleShowAll}
                  style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary)', cursor: 'pointer', font: 'inherit', textDecoration: 'underline' }}
                >
                  show all projects
                </button>
                {' '}to see them, or create a new one.
              </>
            )
            : 'No resources yet. Create one to get started.'}
        </div>
      )}

      {resources.map((r) => (
        <GlassCard key={r.resource_id} className="bkm-resource-card">
          {editingId === r.resource_id ? (
            <>
              <h4 style={{ margin: '0 0 var(--space-3)', color: 'var(--text)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)' }}>
                Edit: {r.name}
              </h4>
              <ResourceForm
                initial={r}
                assignableUsers={assignableUsers}
                onSave={(data) => handleUpdate(r.resource_id, data)}
                onCancel={() => setEditingId(null)}
              />
            </>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 'var(--font-semibold)', color: 'var(--text)', fontSize: 'var(--text-base)' }}>
                    {r.name}
                  </div>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginTop: 'var(--space-1)' }}>
                    {r.timezone}{r.email ? ` · ${r.email}` : ''}{r.project_name ? ` · ${r.project_name}` : ''}
                  </div>
                  {r.blackout_dates && r.blackout_dates.length > 0 && (
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 'var(--space-1)' }}>
                      Blackouts: {r.blackout_dates.join(', ')}
                    </div>
                  )}
                  {((r.calendar_sync_status && r.calendar_sync_status !== 'none') || r.calendar_self_connected) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', marginTop: 'var(--space-1)' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)',
                        ...(r.calendar_self_connected || r.calendar_sync_status === 'connected'
                          ? { background: 'rgba(22,163,74,0.12)', color: 'var(--success, #16a34a)' }
                          : r.calendar_sync_status === 'needs_write_scope'
                          ? { background: 'rgba(217,119,6,0.12)', color: 'var(--warning, #d97706)' }
                          : { background: 'rgba(107,114,128,0.12)', color: 'var(--text-secondary)' }),
                      }}>
                        {r.calendar_self_connected ? (
                          <><CheckCircle size={12} /> Calendar: Connected (self-managed)</>
                        ) : (
                          <>
                            {r.calendar_sync_status === 'connected' && <CheckCircle size={12} />}
                            {r.calendar_sync_status === 'needs_write_scope' && <AlertTriangle size={12} />}
                            {r.calendar_sync_status === 'not_connected' && <AlertCircle size={12} />}
                            {r.calendar_sync_status === 'connected' && 'Calendar: Connected'}
                            {r.calendar_sync_status === 'needs_write_scope' && 'Calendar: Needs write access — connect via Integrations'}
                            {r.calendar_sync_status === 'not_connected' && 'Calendar: Not connected — connect via Integrations'}
                          </>
                        )}
                      </div>
                      {r.assigned_user && (
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                          Calendar owner: {r.assigned_user.full_name}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <GlassButton variant="secondary" onClick={() => { setEditingId(r.resource_id); setCreating(false); }}>
                    <Edit2 size={14} /> Edit
                  </GlassButton>
                  <GlassButton variant="secondary" onClick={() => setExpandedAvail(expandedAvail === r.resource_id ? null : r.resource_id)}>
                    <Calendar size={14} /> Availability
                  </GlassButton>
                  <GlassButton
                    variant="secondary"
                    disabled={!r.project_id}
                    title={!r.project_id ? 'Assign this resource to a project first' : undefined}
                    onClick={() => setBrandingResource(r)}
                  >
                    Branding
                  </GlassButton>
                  <GlassButton
                    variant="secondary"
                    disabled={busyAction === `${r.resource_id}:invite`}
                    onClick={() => handleSendInvite(r.resource_id)}
                  >
                    <Mail size={14} /> {busyAction === `${r.resource_id}:invite` ? 'Sending...' : 'Send calendar invite'}
                  </GlassButton>
                  {(r.calendar_self_connected || r.has_availability_link) && (
                    <GlassButton
                      variant="secondary"
                      disabled={busyAction === `${r.resource_id}:avail`}
                      onClick={() => handleRegenerateAvailability(r.resource_id)}
                    >
                      <Link2 size={14} /> {busyAction === `${r.resource_id}:avail` ? 'Working...' : 'Regenerate availability link'}
                    </GlassButton>
                  )}
                  {r.calendar_self_connected && (
                    <GlassButton
                      variant="danger"
                      disabled={busyAction === `${r.resource_id}:revoke`}
                      onClick={() => handleRevoke(r.resource_id)}
                    >
                      <XCircle size={14} /> {busyAction === `${r.resource_id}:revoke` ? 'Revoking...' : 'Revoke calendar'}
                    </GlassButton>
                  )}
                  <GlassButton variant="secondary" onClick={() => handleDelete(r.resource_id)}>
                    <Trash2 size={14} />
                  </GlassButton>
                </div>
              </div>

              {actionError && (
                <div className="fi-error" style={{ marginTop: 'var(--space-3)' }}>
                  <AlertCircle size={14} /> {actionError}
                </div>
              )}

              {linkInfo[r.resource_id]?.connectPath && (
                <div style={{ marginTop: 'var(--space-3)', borderTop: '1px solid var(--stroke)', paddingTop: 'var(--space-3)' }}>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginBottom: 'var(--space-1)' }}>
                    Calendar connect link{linkInfo[r.resource_id]?.connectEmailSent ? ' (also emailed):' : ' (email not sent — share manually):'}
                  </div>
                  <div className="fi-invite-url" style={{ fontSize: 'var(--text-xs)', wordBreak: 'break-all' }}>
                    {window.location.origin + linkInfo[r.resource_id]!.connectPath}
                  </div>
                </div>
              )}

              {linkInfo[r.resource_id]?.availabilityPath && (
                <div style={{ marginTop: 'var(--space-3)', borderTop: '1px solid var(--stroke)', paddingTop: 'var(--space-3)' }}>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginBottom: 'var(--space-1)' }}>
                    Availability link{linkInfo[r.resource_id]?.availEmailSent ? ' (also emailed):' : ' (email not sent — share manually):'}
                  </div>
                  <div className="fi-invite-url" style={{ fontSize: 'var(--text-xs)', wordBreak: 'break-all' }}>
                    {window.location.origin + linkInfo[r.resource_id]!.availabilityPath}
                  </div>
                </div>
              )}

              {expandedAvail === r.resource_id && (
                <div style={{ marginTop: 'var(--space-4)', borderTop: '1px solid var(--stroke)', paddingTop: 'var(--space-4)' }}>
                  <AvailabilityEditor
                    resourceId={r.resource_id}
                    initialRows={r.availability ?? []}
                    onSaved={(rows) => onUpdated({ ...r, availability: rows })}
                  />
                </div>
              )}
            </>
          )}
        </GlassCard>
      ))}

      {brandingResource && brandingResource.project_id && (
        <BookingBrandingModal
          isOpen={true}
          onClose={() => setBrandingResource(null)}
          projectId={brandingResource.project_id}
          projectName={brandingResource.project_name ?? undefined}
        />
      )}
    </div>
  );
};

// ─── TypeForm ─────────────────────────────────────────────────────────────────

interface TypeFormProps {
  initial?: Partial<BookingTypeItem>;
  resources: BookingResourceItem[];
  onSave: (data: any) => Promise<void>;
  onCancel: () => void;
}

const TypeForm: React.FC<TypeFormProps> = ({ initial, resources, onSave, onCancel }) => {
  const [resourceId, setResourceId] = useState(initial?.resource_id ?? (resources[0]?.resource_id ?? ''));
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [duration, setDuration] = useState(String(initial?.duration_minutes ?? 60));
  const [buffer, setBuffer] = useState(String(initial?.buffer_minutes ?? 15));
  const [minNotice, setMinNotice] = useState(String(initial?.min_notice_hours ?? 24));
  const [maxAdvance, setMaxAdvance] = useState(String(initial?.max_advance_days ?? 30));
  const [location, setLocation] = useState(initial?.location ?? '');
  const [requirePhone, setRequirePhone] = useState(initial?.require_phone ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Name is required.'); return; }
    if (!resourceId) { setError('Resource is required.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave({
        resource_id: resourceId,
        name: name.trim(),
        description: description.trim() || undefined,
        duration_minutes: parseInt(duration, 10) || 60,
        buffer_minutes: parseInt(buffer, 10) || 0,
        min_notice_hours: parseInt(minNotice, 10) || 0,
        max_advance_days: parseInt(maxAdvance, 10) || 30,
        location: location.trim() || undefined,
        require_phone: requirePhone,
      });
    } catch (e: any) {
      setError(getApiError(e, 'Save failed.'));
    } finally {
      setSaving(false);
    }
  };

  const resourceOptions = resources.map((r) => ({ value: r.resource_id, label: r.name }));

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <GlassSelect label="Resource *" value={resourceId} onChange={setResourceId} options={resourceOptions} />
      <GlassInput label="Name *" value={name} onChange={setName} placeholder="e.g. 30-min Consultation" />
      <GlassInput label="Description" value={description} onChange={setDescription} placeholder="Optional description" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
        <GlassInput label="Duration (mins)" value={duration} onChange={setDuration} placeholder="60" />
        <GlassInput label="Buffer (mins)" value={buffer} onChange={setBuffer} placeholder="15" />
        <GlassInput label="Min Notice (hours)" value={minNotice} onChange={setMinNotice} placeholder="24" />
        <GlassInput label="Max Advance (days)" value={maxAdvance} onChange={setMaxAdvance} placeholder="30" />
      </div>
      <GlassInput label="Location (optional)" value={location} onChange={setLocation} placeholder="e.g. Zoom, Office Room 1" />
      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--text)', fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={requirePhone}
          onChange={(e) => setRequirePhone(e.target.checked)}
        />
        <span>Require phone number</span>
      </label>
      {error && (
        <div className="fi-error" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <GlassButton type="submit" variant="primary" disabled={saving}>
          {saving ? 'Saving...' : 'Save Booking Type'}
        </GlassButton>
        <GlassButton type="button" variant="secondary" onClick={onCancel}>Cancel</GlassButton>
      </div>
    </form>
  );
};

// ─── BookingTypesTab ──────────────────────────────────────────────────────────

interface BookingTypesTabProps {
  types: BookingTypeItem[];
  resources: BookingResourceItem[];
  loading: boolean;
  error: string;
  onRefresh: () => void;
  onCreated: (t: BookingTypeItem) => void;
  onUpdated: (t: BookingTypeItem) => void;
  onDeleted: (id: string) => void;
  activeProjectId?: string;
  activeProjectName?: string | null;
  showAllProjects: boolean;
  onToggleShowAll: () => void;
}

const BookingTypesTab: React.FC<BookingTypesTabProps> = ({
  types, resources, loading, error, onRefresh, onCreated, onUpdated, onDeleted,
  activeProjectId, activeProjectName, showAllProjects, onToggleShowAll,
}) => {
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState('');

  // Project -> active custom domain name (or null if none). Fetched once per
  // distinct set of project_ids referenced by the currently-visible resources,
  // so booking type URLs can point at a project's connected branded domain
  // (booking-custom-domain-routing feature) instead of always aime.clearbyte.au.
  const [domainByProject, setDomainByProject] = useState<Record<string, string | null>>({});

  const resourceProjectIds = useMemo(
    () => Array.from(new Set(resources.map((r) => r.project_id).filter((id): id is string => !!id))),
    [resources]
  );

  useEffect(() => {
    if (resourceProjectIds.length === 0) {
      setDomainByProject({});
      return;
    }
    let cancelled = false;
    Promise.all(
      resourceProjectIds.map((pid) =>
        getCustomDomains(pid)
          .then((domains) => {
            const active = (domains || []).find((d: any) => d.status === 'active');
            return [pid, active ? (active.domain as string) : null] as const;
          })
          .catch(() => [pid, null] as const)
      )
    ).then((entries) => {
      if (!cancelled) setDomainByProject(Object.fromEntries(entries));
    });
    return () => { cancelled = true; };
  }, [resourceProjectIds]);

  const bookingUrl = (typeId: string, resourceId: string) => {
    const projectId = resources.find((r) => r.resource_id === resourceId)?.project_id;
    const activeDomain = projectId ? domainByProject[projectId] : null;
    return activeDomain
      ? `https://${activeDomain}/bookings/${typeId}`
      : `${window.location.origin}/book/${typeId}`;
  };

  const copyUrl = (typeId: string, resourceId: string) => {
    navigator.clipboard.writeText(bookingUrl(typeId, resourceId)).then(() => {
      setCopied(typeId);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const handleCreate = async (data: any) => {
    const t = await bookingApi.createType(data);
    onCreated(t);
    setCreating(false);
  };

  const handleUpdate = async (id: string, data: any) => {
    const t = await bookingApi.updateType(id, data);
    onUpdated(t);
    setEditingId(null);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this booking type?')) return;
    setDeleteError('');
    try {
      await bookingApi.deleteType(id);
      onDeleted(id);
    } catch (e: any) {
      setDeleteError(getApiError(e, 'Delete failed.'));
    }
  };

  const resourceName = (id: string) => resources.find((r) => r.resource_id === id)?.name ?? id;

  if (loading) return <div style={{ color: 'var(--text-secondary)', padding: 'var(--space-4)' }}>Loading booking types...</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
        <GlassButton variant="primary" onClick={() => { setCreating(true); setEditingId(null); }} disabled={resources.length === 0}>
          <Plus size={16} /> New Booking Type
        </GlassButton>
        <GlassButton variant="secondary" onClick={onRefresh}>
          <RefreshCw size={14} /> Refresh
        </GlassButton>
      </div>

      {activeProjectId && (
        <ProjectFilterBar activeProjectName={activeProjectName} showAllProjects={showAllProjects} onToggle={onToggleShowAll} />
      )}

      {error && <div className="fi-error"><AlertCircle size={14} /> {error}</div>}
      {deleteError && <div className="fi-error"><AlertCircle size={14} /> {deleteError}</div>}

      {resources.length === 0 && (
        <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
          {activeProjectId && !showAllProjects ? (
            <>
              No resources available for this project (and none unassigned) — a booking type needs a resource.{' '}
              <button
                type="button"
                onClick={onToggleShowAll}
                style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary)', cursor: 'pointer', font: 'inherit', textDecoration: 'underline' }}
              >
                Show all projects
              </button>
              {' '}or create a resource first.
            </>
          ) : (
            'No resources available — create one in the Resources tab first.'
          )}
        </div>
      )}

      {creating && (
        <GlassCard>
          <h4 style={{ margin: '0 0 var(--space-3)', color: 'var(--text)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)' }}>
            New Booking Type
          </h4>
          <TypeForm resources={resources} onSave={handleCreate} onCancel={() => setCreating(false)} />
        </GlassCard>
      )}

      {types.length === 0 && !creating && resources.length > 0 && (
        <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
          {activeProjectId && !showAllProjects ? (
            <>
              No booking types for this project's resources yet.{' '}
              <button
                type="button"
                onClick={onToggleShowAll}
                style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary)', cursor: 'pointer', font: 'inherit', textDecoration: 'underline' }}
              >
                Show all projects
              </button>
              {' '}to see types from other projects, or create one to get started.
            </>
          ) : (
            'No booking types yet. Create one to get started.'
          )}
        </div>
      )}

      {types.map((t) => (
        <GlassCard key={t.booking_type_id}>
          {editingId === t.booking_type_id ? (
            <>
              <h4 style={{ margin: '0 0 var(--space-3)', color: 'var(--text)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)' }}>
                Edit: {t.name}
              </h4>
              <TypeForm
                initial={t}
                resources={resources}
                onSave={(data) => handleUpdate(t.booking_type_id, data)}
                onCancel={() => setEditingId(null)}
              />
            </>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 'var(--font-semibold)', color: 'var(--text)', fontSize: 'var(--text-base)' }}>
                    {t.name}
                  </div>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginTop: 'var(--space-1)' }}>
                    {resourceName(t.resource_id)} · {t.duration_minutes} min
                    {t.location ? ` · ${t.location}` : ''}
                  </div>
                  {t.description && (
                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginTop: 'var(--space-1)' }}>
                      {t.description}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <GlassButton variant="secondary" onClick={() => { setEditingId(t.booking_type_id); setCreating(false); }}>
                    <Edit2 size={14} /> Edit
                  </GlassButton>
                  <GlassButton variant="secondary" onClick={() => handleDelete(t.booking_type_id)}>
                    <Trash2 size={14} />
                  </GlassButton>
                </div>
              </div>

              <div style={{ marginTop: 'var(--space-3)', borderTop: '1px solid var(--stroke)', paddingTop: 'var(--space-3)' }}>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginBottom: 'var(--space-1)' }}>
                  Public booking URL:
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <div className="fi-invite-url" style={{ flex: 1, fontSize: 'var(--text-xs)' }}>
                    {bookingUrl(t.booking_type_id, t.resource_id)}
                  </div>
                  <GlassButton variant="secondary" onClick={() => copyUrl(t.booking_type_id, t.resource_id)}>
                    <Copy size={14} /> {copied === t.booking_type_id ? 'Copied!' : 'Copy'}
                  </GlassButton>
                </div>
              </div>
            </>
          )}
        </GlassCard>
      ))}
    </div>
  );
};

// ─── AppointmentsTab ──────────────────────────────────────────────────────────

interface AppointmentsTabProps {
  appointments: Appointment[];
  loading: boolean;
  error: string;
  onRefresh: () => void;
  onStatusUpdated: (id: string, status: string) => void;
}

const AppointmentsTab: React.FC<AppointmentsTabProps> = ({
  appointments, loading, error, onRefresh, onStatusUpdated,
}) => {
  const [updating, setUpdating] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState('');

  const changeStatus = async (id: string, status: 'cancelled' | 'completed' | 'no_show' | 'booked') => {
    setUpdating(id);
    setUpdateError('');
    try {
      await bookingApi.updateAppointmentStatus(id, status);
      onStatusUpdated(id, status);
    } catch (e: any) {
      setUpdateError(getApiError(e, 'Failed to update status.'));
    } finally {
      setUpdating(null);
    }
  };

  if (loading) return <div style={{ color: 'var(--text-secondary)', padding: 'var(--space-4)' }}>Loading appointments...</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <GlassButton variant="secondary" onClick={onRefresh}>
          <RefreshCw size={14} /> Refresh
        </GlassButton>
      </div>

      {error && <div className="fi-error"><AlertCircle size={14} /> {error}</div>}
      {updateError && <div className="fi-error"><AlertCircle size={14} /> {updateError}</div>}

      {appointments.length === 0 && (
        <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>No appointments yet.</div>
      )}

      <div className="bkm-appt-table-wrapper">
        {appointments.length > 0 && (
          <table className="fi-submissions-table">
            <thead>
              <tr>
                <th>Attendee</th>
                <th>Booking Type</th>
                <th>Resource</th>
                <th>Start</th>
                <th>Status</th>
                <th>CRM</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {appointments.map((a) => (
                <tr key={a.appointment_id}>
                  <td>
                    <div style={{ fontWeight: 'var(--font-medium)', color: 'var(--text)' }}>
                      {a.attendee_name ?? '—'}
                    </div>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                      {a.attendee_email ?? ''}
                    </div>
                  </td>
                  <td style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>
                    {a.booking_type_name ?? '—'}
                  </td>
                  <td style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>
                    {a.resource_name ?? '—'}
                  </td>
                  <td style={{ fontSize: 'var(--text-sm)', color: 'var(--text)', whiteSpace: 'nowrap' }}>
                    {a.start_utc && a.timezone
                      ? formatUtcInTimezone(a.start_utc, a.timezone)
                      : a.start_utc ?? '—'}
                  </td>
                  <td>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-semibold)',
                      background: `${STATUS_COLORS[a.status] ?? 'var(--text-secondary)'}22`,
                      color: STATUS_COLORS[a.status] ?? 'var(--text-secondary)',
                    }}>
                      {a.status}
                    </span>
                  </td>
                  <td style={{ fontSize: 'var(--text-sm)' }}>
                    {a.crm_meeting_id
                      ? <span title="Synced" style={{ color: 'var(--success, #16a34a)' }}><CheckCircle size={16} /></span>
                      : a.crm_sync_error
                        ? <span title={a.crm_sync_error} style={{ color: 'var(--danger)' }}>
                            <XCircle size={16} />
                          </span>
                        : <span style={{ color: 'var(--text-secondary)' }}>—</span>
                    }
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
                      {a.status !== 'cancelled' && (
                        <GlassButton
                          variant="secondary"
                          disabled={updating === a.appointment_id}
                          onClick={() => changeStatus(a.appointment_id, 'cancelled')}
                        >
                          Cancel
                        </GlassButton>
                      )}
                      {a.status !== 'completed' && (
                        <GlassButton
                          variant="secondary"
                          disabled={updating === a.appointment_id}
                          onClick={() => changeStatus(a.appointment_id, 'completed')}
                        >
                          Completed
                        </GlassButton>
                      )}
                      {a.status !== 'no_show' && (
                        <GlassButton
                          variant="secondary"
                          disabled={updating === a.appointment_id}
                          onClick={() => changeStatus(a.appointment_id, 'no_show')}
                        >
                          No-show
                        </GlassButton>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ─── BookingManager (main) ────────────────────────────────────────────────────

type BkmTab = 'resources' | 'types' | 'appointments';

interface BkmOutletContext {
  selectedProjectId?: string;
  onProjectSelect?: (id?: string) => void;
}

const BookingManager: React.FC = () => {
  const { selectedProjectId } = useOutletContext<BkmOutletContext>();
  const [activeTab, setActiveTab] = useState<BkmTab>('resources');

  // Convenience default: filter resource selection to the active project (+ unassigned
  // resources, which are always shown). "Show all projects" is a simple, non-persisted,
  // per-session override — nothing is ever permanently hidden.
  const [showAllProjects, setShowAllProjects] = useState(false);
  const effectiveProjectId = showAllProjects ? undefined : selectedProjectId;

  const [projects, setProjects] = useState<Project[]>([]);
  useEffect(() => { getProjects(false).then(setProjects).catch(() => {}); }, []);
  const activeProjectName = useMemo(
    () => (selectedProjectId ? projects.find((p) => p.project_id === selectedProjectId)?.name ?? null : null),
    [projects, selectedProjectId]
  );

  const [resources, setResources] = useState<BookingResourceItem[]>([]);
  const [resourcesLoading, setResourcesLoading] = useState(true);
  const [resourcesError, setResourcesError] = useState('');

  const [types, setTypes] = useState<BookingTypeItem[]>([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [typesError, setTypesError] = useState('');

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [apptsLoading, setApptsLoading] = useState(true);
  const [apptsError, setApptsError] = useState('');

  const loadResources = useCallback(async () => {
    setResourcesLoading(true);
    setResourcesError('');
    try {
      setResources(await bookingApi.listResources(effectiveProjectId));
    } catch (e: any) {
      setResourcesError(getApiError(e, 'Failed to load resources.'));
    } finally {
      setResourcesLoading(false);
    }
  }, [effectiveProjectId]);

  const loadTypes = useCallback(async () => {
    setTypesLoading(true);
    setTypesError('');
    try {
      setTypes(await bookingApi.listTypes());
    } catch (e: any) {
      setTypesError(getApiError(e, 'Failed to load booking types.'));
    } finally {
      setTypesLoading(false);
    }
  }, []);

  const loadAppointments = useCallback(async () => {
    setApptsLoading(true);
    setApptsError('');
    try {
      setAppointments(await bookingApi.listAppointments());
    } catch (e: any) {
      setApptsError(getApiError(e, 'Failed to load appointments.'));
    } finally {
      setApptsLoading(false);
    }
  }, []);

  useEffect(() => { loadResources(); }, [loadResources]);
  useEffect(() => { loadTypes(); }, [loadTypes]);
  useEffect(() => { loadAppointments(); }, [loadAppointments]);

  // BookingType has no project_id of its own — it's only reachable transitively via
  // resource_id → BookingResource.project_id. `resources` is already filtered per the
  // active-project default (or unfiltered when "Show all projects" is on), so deriving
  // visible types from the currently-loaded resource set keeps both tabs and the
  // booking-type resource dropdown consistent with a single filter state.
  const visibleResourceIds = useMemo(() => new Set(resources.map((r) => r.resource_id)), [resources]);
  const visibleTypes = useMemo(
    () => types.filter((t) => visibleResourceIds.has(t.resource_id)),
    [types, visibleResourceIds]
  );

  const tabs = [
    { id: 'resources', label: 'Resources' },
    { id: 'types', label: 'Booking Types' },
    { id: 'appointments', label: 'Appointments' },
  ];

  return (
    <div className="fi-module-root">
      <div style={{ marginBottom: 'var(--space-5)' }}>
        <h1 style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)', color: 'var(--text)', margin: 0, display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <Calendar size={24} /> Booking Management
        </h1>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', margin: 'var(--space-1) 0 0' }}>
          Manage booking resources, types, and appointments.
        </p>
      </div>

      <div style={{ marginBottom: 'var(--space-5)' }}>
        <GlassTabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={(id) => setActiveTab(id as BkmTab)}
        />
      </div>

      {activeTab === 'resources' && (
        <ResourcesTab
          resources={resources}
          loading={resourcesLoading}
          error={resourcesError}
          onRefresh={loadResources}
          onCreated={(r) => setResources((prev) => [...prev, r])}
          onUpdated={(r) => setResources((prev) => prev.map((x) => x.resource_id === r.resource_id ? r : x))}
          onDeleted={(id) => setResources((prev) => prev.filter((x) => x.resource_id !== id))}
          activeProjectId={selectedProjectId}
          activeProjectName={activeProjectName}
          showAllProjects={showAllProjects}
          onToggleShowAll={() => setShowAllProjects((v) => !v)}
        />
      )}

      {activeTab === 'types' && (
        <BookingTypesTab
          types={visibleTypes}
          resources={resources}
          loading={typesLoading}
          error={typesError}
          onRefresh={loadTypes}
          onCreated={(t) => setTypes((prev) => [...prev, t])}
          onUpdated={(t) => setTypes((prev) => prev.map((x) => x.booking_type_id === t.booking_type_id ? t : x))}
          onDeleted={(id) => setTypes((prev) => prev.filter((x) => x.booking_type_id !== id))}
          activeProjectId={selectedProjectId}
          activeProjectName={activeProjectName}
          showAllProjects={showAllProjects}
          onToggleShowAll={() => setShowAllProjects((v) => !v)}
        />
      )}

      {activeTab === 'appointments' && (
        <AppointmentsTab
          appointments={appointments}
          loading={apptsLoading}
          error={apptsError}
          onRefresh={loadAppointments}
          onStatusUpdated={(id, status) =>
            setAppointments((prev) =>
              prev.map((a) => a.appointment_id === id ? { ...a, status } : a)
            )
          }
        />
      )}
    </div>
  );
};

export default BookingManager;
